<template>
  <BaseWidget
    class="traditional-channel-table data-table"
    :title="content.title"
    :widgetId="content.id"
    :edit="edit"
    :persistent="true"
    :content="content"
    :isWidgetLoading="false"
    :getWidgetReport="getWidgetReport"
  >
    <template v-if="rows.length" v-slot:widget-control>
      <div class="traditional-channel-table__controls">
        <v-select
          class="traditional-channel-table__rows-select"
          :items="tableRows"
          :value="rowsPerPage"
          @change="onUpdateItemRows"
          label="Rows"
          item-text="title"
          item-value="value"
          height="48"
          hide-details
          outlined
        />
      </div>
    </template>

    <div v-if="!rows.length" class="traditional-channel-table__placeholder">
      {{ Lang.noData }}
    </div>
    <BaseTable
      v-else
      :data="rows"
      :headers="headers"
      :rowsPerPage="rowsPerPage"
      :content="content"
      :footer-props="footerProps"
      :module="PLAN_MODULE"
      :draggable-columns="false"
      isTableWidget
    >
      <template v-slot:[`item.market`]="{ item }">{{ item.market }}</template>
      <template v-slot:[`item.dmaCode`]="{ item }">{{ item.dmaCode }}</template>
      <template v-slot:[`item.value`]="{ item }">{{ formatScore(item.value) }}</template>
      <template v-slot:[`item.bucket`]="{ item }">{{ item.bucket || "-" }}</template>
    </BaseTable>
  </BaseWidget>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";

import BaseTable from "@/components/tables/BaseTable.vue";
import BaseWidget from "@/components/widgets/plan/BaseWidget.vue";
import { PLAN_MODULE } from "@/constants";
import { expandTraditionalViabilityV2 } from "@/helpers/traditionalViability";
import Lang from "@/lang";
import { ProjectService } from "@/services/projectService";
import { tableRows } from "@/settings/tableSettings";
import { ContentWidget } from "@/types/layouts";
import { Project, TraditionalGrpWeekRow } from "@/types/project";

type Mode = "grp" | "viability";
const CHART_CONFIG: Record<string, { channel: string; mode: Mode }> = {
  traditionalTvGrpScoreTable: { channel: "Television", mode: "grp" },
  traditionalRadioGrpScoreTable: { channel: "Radio", mode: "grp" },
  traditionalTvViabilityTable: { channel: "Television", mode: "viability" },
  traditionalRadioViabilityTable: { channel: "Radio", mode: "viability" },
};

type TableRow = { market: string; dmaCode: string; value: number | null; bucket: string | null };

export default Vue.extend({
  name: "TraditionalChannelTableWidget",

  components: { BaseWidget, BaseTable },

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
  },

  data() {
    return {
      Lang,
      PLAN_MODULE,
      tableRows,
      rowsPerPage: 25 as number,
      footerProps: {
        showCurrentPage: true,
        disableItemsPerPage: true,
        disablePagination: false,
      },
    };
  },

  computed: {
    ...mapGetters("projectOutput", ["project"]),

    config(): { channel: string; mode: Mode } | null {
      return CHART_CONFIG[this.content?.chartType] ?? null;
    },

    isViability(): boolean {
      return this.config?.mode === "viability";
    },

    scenarioId(): string {
      return String(this.content?.settings?.scenarioId ?? "");
    },

    planMarketsFirst(): boolean {
      return this.content?.settings?.planMarketsFirst !== false;
    },

    headers(): Array<{ text: string; value: string; sortable?: boolean; width?: string }> {
      const cols: Array<{ text: string; value: string; sortable?: boolean; width?: string }> = [
        { text: Lang.marketLabel, value: "market" },
        { text: Lang.dmaCodeLabel, value: "dmaCode" },
        { text: this.isViability ? Lang.marketViabilityScoreLabel : Lang.grpPerWeekScoreLabel, value: "value" },
      ];
      if (this.isViability) {
        cols.push({ text: Lang.bucketLabel, value: "bucket", width: "180px" });
      }
      return cols;
    },

    rows(): TableRow[] {
      const project = this.project as Project | null;
      const config = this.config;
      if (!project || !this.scenarioId || !config) return [];

      const scenario = new ProjectService(project).getScenarioById(this.scenarioId);
      const output = scenario?.output;

      let result: TableRow[];
      if (config.mode === "viability") {
        result = expandTraditionalViabilityV2(output?.traditional_viability_v2)
          .filter((row) => row.channel_name === config.channel && String(row.scenario_id) === this.scenarioId)
          .map((row) => ({
            market: row.dma_name,
            dmaCode: row.dma_code,
            value: row.viability_score,
            bucket: row.viability_bucket,
          }));
      } else {
        const grpRows = (output?.traditional_grp_week_rows ?? []) as TraditionalGrpWeekRow[];
        result = grpRows
          .filter((row) => row.channel_name === config.channel && String(row.scenario_id) === this.scenarioId)
          .map((row) => ({ market: row.dma_name, dmaCode: row.dma_code, value: row.grp_per_week_score, bucket: null }));
      }

      return this.planMarketsFirst ? this.orderPlanMarketsFirst(result, scenario?.dma ?? []) : result;
    },
  },

  methods: {
    getWidgetReport(): void {
      return undefined;
    },

    onUpdateItemRows(val: number): void {
      this.rowsPerPage = val;
    },

    orderPlanMarketsFirst(rows: TableRow[], scenarioDma: string[]): TableRow[] {
      const norm = (value: string): string => String(value).trim().toUpperCase();
      const included = new Set(scenarioDma.map(norm));
      if (!included.size) return rows;

      const inPlan: TableRow[] = [];
      const rest: TableRow[] = [];
      for (const row of rows) {
        const isIncluded = included.has(norm(row.dmaCode)) || included.has(norm(row.market));
        (isIncluded ? inPlan : rest).push(row);
      }
      return [...inPlan, ...rest];
    },

    formatScore(value: number | null): string {
      return typeof value === "number" ? value.toFixed(2) : "-";
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme";
@import "@/scss/custom_variables.scss";

.traditional-channel-table {
  &__placeholder {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    opacity: 0.6;
  }

  &__controls {
    display: flex;
    align-items: center;
  }

  &__rows-select {
    margin-right: 10px !important;
    border-radius: 4px;
    max-width: 110px;
  }
}
</style>
